import { RootState } from '@/lib/store/store';
import { IProductVariant } from '@/lib/types/core/product-variant';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    setError,
    setLoading,
    setSelectedVariant,
    setVariants,
    updateVariant
} from '../features/product-variants/product-variants-slice';

/**
 * Hook for interacting with product variants in the Redux store
 */
export const useProductVariants = () => {
  const dispatch = useDispatch();
  const {
    variants,
    selectedVariant,
    loading,
    error,
    mediaUploading,
    mediaError
  } = useSelector((state: RootState) => state.productVariants);

  // Set all variants
  const setAllVariants = useCallback((variants: IProductVariant[]) => {
    dispatch(setVariants(variants));
  }, [dispatch]);

  // Update a single variant
  const updateSingleVariant = useCallback((variant: IProductVariant) => {
    dispatch(updateVariant(variant));
  }, [dispatch]);

  // Select a variant
  const selectVariant = useCallback((variant: IProductVariant) => {
    dispatch(setSelectedVariant(variant));
  }, [dispatch]);

  // Set loading state
  const setLoadingState = useCallback((isLoading: boolean) => {
    dispatch(setLoading(isLoading));
  }, [dispatch]);

  // Set error state
  const setErrorState = useCallback((errorMessage: string) => {
    dispatch(setError(errorMessage));
  }, [dispatch]);

  // Clear error
  const clearError = useCallback(() => {
    dispatch(setError(null));
  }, [dispatch]);

  // Get variants by product ID
  const getVariantsByProductId = useCallback((productId: string) => {
    return variants.filter((item: IProductVariant) => item.productId === productId);
  }, [variants]);

  return {
    // State
    variants,
    selectedVariant,
    loading,
    error,
    mediaUploading,
    mediaError,

    // Actions
    setVariants: setAllVariants,
    updateVariant: updateSingleVariant,
    selectVariant,
    setLoading: setLoadingState,
    setError: setErrorState,
    clearError,

    // Helper methods
    getVariantsByProductId
  };
};
